// In-memory stock documents for demo mode (no Supabase configured). The state
// machine and numbering behave like the database; stock levels are not
// recalculated in demo mode.

import type { Page } from "@/components/DataList";
import type {
  AdjustmentDocument,
  AdjustmentLineInput,
  AdjustmentReason,
  DocumentRow,
  DocumentStatus,
  DocumentType,
  PostResult,
  StatusFilter,
  TransferDocument,
  TransferLineInput,
} from "./api";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const now = () => new Date().toISOString();
const year = new Date().getFullYear();
let seq = 0;
const counters: Record<DocumentType, number> = { stock_adjustment: 1, stock_transfer: 1 };
const prefixes: Record<DocumentType, string> = { stock_adjustment: "ADJ", stock_transfer: "TR" };

const reasons: AdjustmentReason[] = [
  { code: "CORRECTION", description: "Korekta", direction: "both", is_active: true },
  { code: "DAMAGED", description: "Uszkodzenie", direction: "out", is_active: true },
  { code: "FOUND", description: "Znaleziony towar", direction: "in", is_active: true },
  { code: "OPENING", description: "Bilans otwarcia", direction: "in", is_active: true },
  { code: "SCRAP", description: "Złomowanie", direction: "out", is_active: true },
];

interface Stored {
  type: DocumentType;
  id: string;
  number: string | null;
  status: DocumentStatus;
  note: string | null;
  reason_code?: string;
  reverses_adjustment_id?: string | null;
  created_at: string;
  posted_at: string | null;
  adjustmentLines: AdjustmentLineInput[];
  transferLines: TransferLineInput[];
}

const docs: Stored[] = [];

function nextNumber(type: DocumentType): string {
  return `${prefixes[type]}-${year}-${String(counters[type]++).padStart(5, "0")}`;
}

const stateError = (status: DocumentStatus) =>
  Object.assign(new Error(`Only draft documents can be changed (status: ${status})`), { code: "23514" });

function toRow(d: Stored): DocumentRow {
  const reason = reasons.find((r) => r.code === d.reason_code);
  return {
    id: d.id,
    number: d.number,
    status: d.status,
    note: d.note,
    created_at: d.created_at,
    posted_at: d.posted_at,
    company: { code: "PL01" },
    ...(d.type === "stock_adjustment"
      ? { reason_code: d.reason_code!, reason: reason ? { description: reason.description } : null, reverses_adjustment_id: d.reverses_adjustment_id ?? null }
      : {}),
  };
}

const code = (id: string) => id.replace(/^(item|wh|loc)-/, "");

export const demoApi = {
  async list(type: DocumentType, status: StatusFilter, cursor: string | null): Promise<Page<DocumentRow>> {
    await delay(60);
    const rows = docs
      .filter((d) => d.type === type && (status === "all" || d.status === status))
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .filter((d) => !cursor || `${d.created_at}|${d.id}` < cursor)
      .map(toRow);
    return { rows: rows.slice(0, 50), nextCursor: null };
  },
  async get(type: DocumentType, id: string): Promise<AdjustmentDocument | TransferDocument | null> {
    await delay(40);
    const d = docs.find((x) => x.type === type && x.id === id);
    if (!d) return null;
    const base = {
      id: d.id,
      number: d.number,
      status: d.status,
      company_id: "demo-pl01",
      company_code: "PL01",
      note: d.note,
      created_at: d.created_at,
      posted_at: d.posted_at,
      updated_at: d.created_at,
    };
    if (type === "stock_adjustment") {
      const reason = reasons.find((r) => r.code === d.reason_code)!;
      const original = docs.find((x) => x.id === d.reverses_adjustment_id);
      const reversal = docs.find((x) => x.reverses_adjustment_id === d.id);
      return {
        ...base,
        type,
        reason_code: reason.code,
        reason_description: reason.description,
        reason_direction: reason.direction,
        reverses_adjustment_id: d.reverses_adjustment_id ?? null,
        reverses_number: original?.number ?? null,
        reversed_by: reversal ? { id: reversal.id, number: reversal.number ?? "" } : null,
        line_count: d.adjustmentLines.length,
        lines: d.adjustmentLines.map((l, i) => ({
          id: `${d.id}-${i}`,
          line_no: i + 1,
          item_id: l.item_id,
          item_code: code(l.item_id),
          item_description: code(l.item_id),
          lot_controlled: !!l.lot_code || !!l.lot_id,
          warehouse_id: l.warehouse_id,
          warehouse_code: code(l.warehouse_id),
          location_id: l.location_id ?? "",
          location_code: l.location_id ? "—" : "DEFAULT",
          lot_id: l.lot_id ?? null,
          lot_code: l.lot_code ?? null,
          expiry_date: l.expiry_date ?? null,
          qty: l.qty,
          uom_id: l.uom_id ?? "uom-PCS",
          uom_code: "PCS",
          uom_decimals: 0,
          transaction_id: d.status === "posted" ? i + 1 : null,
        })),
      };
    }
    return {
      ...base,
      type,
      lines: d.transferLines.map((l, i) => ({
        id: `${d.id}-${i}`,
        line_no: i + 1,
        item_id: l.item_id,
        item_code: code(l.item_id),
        item_description: code(l.item_id),
        lot_controlled: !!l.lot_id,
        from_warehouse_id: l.from_warehouse_id,
        from_warehouse_code: code(l.from_warehouse_id),
        from_location_id: l.from_location_id ?? "",
        from_location_code: l.from_location_id ? "—" : "DEFAULT",
        to_warehouse_id: l.to_warehouse_id,
        to_warehouse_code: code(l.to_warehouse_id),
        to_location_id: l.to_location_id ?? "",
        to_location_code: l.to_location_id ? "—" : "DEFAULT",
        lot_id: l.lot_id ?? null,
        lot_code: null,
        qty: l.qty,
        uom_id: l.uom_id ?? "uom-PCS",
        uom_code: "PCS",
        uom_decimals: 0,
      })),
    };
  },
  async reasons(): Promise<AdjustmentReason[]> {
    await delay(20);
    return reasons;
  },
  async saveAdjustment(id: string | null, _company: string, reasonCode: string, note: string, lines: AdjustmentLineInput[]): Promise<string> {
    await delay(100);
    if (id) {
      const d = docs.find((x) => x.id === id)!;
      if (d.status !== "draft") throw stateError(d.status);
      Object.assign(d, { reason_code: reasonCode, note: note || null, adjustmentLines: lines });
      return id;
    }
    const doc: Stored = {
      type: "stock_adjustment",
      id: `adj-${++seq}`,
      number: null,
      status: "draft",
      note: note || null,
      reason_code: reasonCode,
      reverses_adjustment_id: null,
      created_at: now(),
      posted_at: null,
      adjustmentLines: lines,
      transferLines: [],
    };
    docs.push(doc);
    return doc.id;
  },
  async post(type: DocumentType, id: string): Promise<PostResult> {
    await delay(150);
    const d = docs.find((x) => x.type === type && x.id === id)!;
    if (d.status !== "draft") throw stateError(d.status);
    if (d.adjustmentLines.length + d.transferLines.length === 0) {
      throw Object.assign(new Error("A document needs at least one line"), { code: "23514" });
    }
    Object.assign(d, { status: "posted", number: nextNumber(type), posted_at: now() });
    return { id, number: d.number!, status: "posted" };
  },
  async cancel(type: DocumentType, id: string): Promise<PostResult> {
    await delay(100);
    const d = docs.find((x) => x.type === type && x.id === id)!;
    if (d.status !== "draft") throw stateError(d.status);
    d.status = "cancelled";
    return { id, status: "cancelled" };
  },
  async reverse(id: string): Promise<PostResult> {
    await delay(150);
    const d = docs.find((x) => x.id === id)!;
    const rev: Stored = {
      ...d,
      id: `adj-${++seq}`,
      number: nextNumber("stock_adjustment"),
      note: null,
      reverses_adjustment_id: id,
      created_at: now(),
      posted_at: now(),
      adjustmentLines: d.adjustmentLines.map((l) => ({ ...l, qty: String(-Number(l.qty)) })),
    };
    docs.push(rev);
    return { id: rev.id, number: rev.number!, status: "posted" };
  },
  async quickTransfer(_company: string, lines: TransferLineInput[], note: string): Promise<PostResult> {
    await delay(150);
    const doc: Stored = {
      type: "stock_transfer",
      id: `tr-${++seq}`,
      number: nextNumber("stock_transfer"),
      status: "posted",
      note: note || null,
      created_at: now(),
      posted_at: now(),
      adjustmentLines: [],
      transferLines: lines,
    };
    docs.push(doc);
    return { id: doc.id, number: doc.number!, status: "posted" };
  },
};
